import { useCallback, useEffect, useRef, useState } from 'react';

import { getUnopenedTicketCount } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useSocket, useSocketReconnect } from '../contexts/SocketContext';
import { onUnopenedQueueChanged } from '../utils/unopenedQueue';

export function useUnopenedTicketCount(): number {
  const { token } = useAuth();
  const { socket } = useSocket();
  const [count, setCount] = useState(0);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => {
    if (!token) {
      setCount(0);
      return;
    }
    getUnopenedTicketCount()
      .then(setCount)
      .catch(() => undefined);
  }, [token]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      refresh();
    }, 200);
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => onUnopenedQueueChanged(scheduleRefresh), [scheduleRefresh]);

  useEffect(() => {
    if (!socket) return;

    socket.on('ticket:new', scheduleRefresh);
    socket.on('ticket:updated', scheduleRefresh);
    socket.on('notification:new', scheduleRefresh);

    return () => {
      socket.off('ticket:new', scheduleRefresh);
      socket.off('ticket:updated', scheduleRefresh);
      socket.off('notification:new', scheduleRefresh);
    };
  }, [socket, scheduleRefresh]);

  useSocketReconnect(refresh);

  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    [],
  );

  return count;
}
